import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { db } from '../firebase';
import { collection, getDocs, query, orderBy, where } from 'firebase/firestore';
import { getAuth, onAuthStateChanged } from 'firebase/auth';

// Create the DataContext
const DataContext = createContext();

// Cache configuration
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const SALES_CACHE_KEY = 'sales_data_cache';
const PRODUCTS_CACHE_KEY = 'products_data_cache';
const SALES_TIMESTAMP_KEY = 'sales_data_timestamp';
const PRODUCTS_TIMESTAMP_KEY = 'products_data_timestamp';
const FULL_LOAD_EXPIRY_KEY = 'sales_full_load_expiry';
const FULL_LOAD_DURATION = 60 * 60 * 1000; // 1 hour

// Helper: get date string N days ago in YYYY-MM-DD format
const getDateDaysAgo = (days) => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().split('T')[0];
};

// Helper function to check if cache is valid
const isCacheValid = (timestampKey) => {
  const timestamp = localStorage.getItem(timestampKey);
  if (!timestamp) return false;
  return Date.now() - parseInt(timestamp) < CACHE_DURATION;
};

// Helper function to get cached data
const getCachedData = (cacheKey) => {
  try {
    const cachedData = localStorage.getItem(cacheKey);
    return cachedData ? JSON.parse(cachedData) : null;
  } catch (error) {
    console.error('Error parsing cached data:', error);
    return null;
  }
};

// Helper function to set cached data
const setCachedData = (cacheKey, timestampKey, data) => {
  try {
    localStorage.setItem(cacheKey, JSON.stringify(data));
    localStorage.setItem(timestampKey, Date.now().toString());
  } catch (error) {
    console.error('Error caching data:', error);
  }
};

// Check if full-load mode is still active
const isFullLoadActive = () => {
  const expiry = localStorage.getItem(FULL_LOAD_EXPIRY_KEY);
  if (!expiry) return false;
  return Date.now() < parseInt(expiry);
};

// DataProvider component
export const DataProvider = ({ children }) => {
  const [salesData, setSalesData] = useState([]);
  const [productsData, setProductsData] = useState([]);
  const [salesLoading, setSalesLoading] = useState(false);
  const [productsLoading, setProductsLoading] = useState(false);
  const [salesError, setSalesError] = useState(null);
  const [productsError, setProductsError] = useState(null);
  const [isFullDataLoaded, setIsFullDataLoaded] = useState(isFullLoadActive());
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const fullLoadTimerRef = useRef(null);

  // Function to fetch sales data — last 5 days by default, or all if full-load active
  const fetchSalesData = useCallback(async (forceRefresh = false, loadAll = false) => {
    if (!user) {
      console.log('🚫 Cannot fetch sales data: User not authenticated');
      setSalesData([]);
      return [];
    }

    const shouldLoadAll = loadAll || isFullLoadActive();

    // Check cache first unless force refresh is requested
    if (!forceRefresh && isCacheValid(SALES_TIMESTAMP_KEY)) {
      const cachedSales = getCachedData(SALES_CACHE_KEY);
      if (cachedSales) {
        console.log('📦 Using cached sales data');
        setSalesData(cachedSales);
        return cachedSales;
      }
    }

    setSalesLoading(true);
    setSalesError(null);

    try {
      let salesQuery;
      if (shouldLoadAll) {
        console.log('🔄 Fetching ALL sales data from Firestore');
        salesQuery = query(collection(db, 'Sales'), orderBy('date', 'desc'));
      } else {
        const fiveDaysAgo = getDateDaysAgo(5);
        console.log('🔄 Fetching last 5 days of sales data (since', fiveDaysAgo, ')');
        salesQuery = query(
          collection(db, 'Sales'),
          where('date', '>=', fiveDaysAgo),
          orderBy('date', 'desc')
        );
      }

      const snapshot = await getDocs(salesQuery);
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setSalesData(data);
      setCachedData(SALES_CACHE_KEY, SALES_TIMESTAMP_KEY, data);
      console.log(`✅ Sales data fetched: ${data.length} records${shouldLoadAll ? ' (full load)' : ' (last 5 days)'}`);
      return data;
    } catch (err) {
      console.error('❌ Error fetching sales data:', err);
      setSalesError(err.message);
      throw err;
    } finally {
      setSalesLoading(false);
    }
  }, [user]);

  // Load all data for 1 hour
  const loadAllData = useCallback(async () => {
    const expiry = Date.now() + FULL_LOAD_DURATION;
    localStorage.setItem(FULL_LOAD_EXPIRY_KEY, expiry.toString());
    setIsFullDataLoaded(true);

    // Clear previous timer
    if (fullLoadTimerRef.current) clearTimeout(fullLoadTimerRef.current);

    // Set timer to revert after 1 hour
    fullLoadTimerRef.current = setTimeout(() => {
      console.log('⏰ Full-load period expired, reverting to last 5 days');
      localStorage.removeItem(FULL_LOAD_EXPIRY_KEY);
      setIsFullDataLoaded(false);
      // Invalidate cache so next fetch uses 5-day window
      localStorage.removeItem(SALES_CACHE_KEY);
      localStorage.removeItem(SALES_TIMESTAMP_KEY);
      if (user) fetchSalesData(true, false);
    }, FULL_LOAD_DURATION);

    // Invalidate cache and fetch all
    localStorage.removeItem(SALES_CACHE_KEY);
    localStorage.removeItem(SALES_TIMESTAMP_KEY);
    await fetchSalesData(true, true);
  }, [user, fetchSalesData]);

  // Revert to 5-day mode
  const unloadAllData = useCallback(async () => {
    localStorage.removeItem(FULL_LOAD_EXPIRY_KEY);
    if (fullLoadTimerRef.current) clearTimeout(fullLoadTimerRef.current);
    setIsFullDataLoaded(false);
    localStorage.removeItem(SALES_CACHE_KEY);
    localStorage.removeItem(SALES_TIMESTAMP_KEY);
    if (user) await fetchSalesData(true, false);
  }, [user, fetchSalesData]);

  // Function to fetch products data with caching
  const fetchProductsData = useCallback(async (forceRefresh = false) => {
    if (!forceRefresh && isCacheValid(PRODUCTS_TIMESTAMP_KEY)) {
      const cachedProducts = getCachedData(PRODUCTS_CACHE_KEY);
      if (cachedProducts) {
        console.log('📦 Using cached products data');
        setProductsData(cachedProducts);
        return cachedProducts;
      }
    }

    setProductsLoading(true);
    setProductsError(null);

    try {
      console.log('🔄 Fetching fresh products data from Firestore');
      const snapshot = await getDocs(collection(db, 'Products'));
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setProductsData(data);
      setCachedData(PRODUCTS_CACHE_KEY, PRODUCTS_TIMESTAMP_KEY, data);
      console.log('✅ Products data fetched and cached');
      return data;
    } catch (err) {
      console.error('❌ Error fetching products data:', err);
      setProductsError(err.message);
      throw err;
    } finally {
      setProductsLoading(false);
    }
  }, []);

  // Function to invalidate cache
  const invalidateCache = (type) => {
    if (type === 'sales' || type === 'all') {
      localStorage.removeItem(SALES_CACHE_KEY);
      localStorage.removeItem(SALES_TIMESTAMP_KEY);
      console.log('🗑️ Sales cache invalidated');
    }
    if (type === 'products' || type === 'all') {
      localStorage.removeItem(PRODUCTS_CACHE_KEY);
      localStorage.removeItem(PRODUCTS_TIMESTAMP_KEY);
      console.log('🗑️ Products cache invalidated');
    }
  };

  // Function to refresh data
  const refreshData = useCallback(async () => {
    const promises = [fetchProductsData(true)];
    if (user) {
      promises.push(fetchSalesData(true));
    }
    await Promise.all(promises);
  }, [user, fetchSalesData, fetchProductsData]);

  // Auth listener
  useEffect(() => {
    const auth = getAuth();
    const unsubscribeAuth = onAuthStateChanged(auth, (authUser) => {
      console.log('🔐 Auth state changed in DataContext:', authUser ? `User: ${authUser.uid}` : 'No user');
      if (authUser) {
        setUser(authUser);
      } else {
        setUser(null);
        setSalesData([]);
      }
      setAuthLoading(false);
    });
    return () => unsubscribeAuth();
  }, []);

  // Data loading on auth ready
  useEffect(() => {
    if (authLoading) return;

    // Restore full-load timer if still active from previous session
    if (isFullLoadActive()) {
      const remaining = parseInt(localStorage.getItem(FULL_LOAD_EXPIRY_KEY)) - Date.now();
      setIsFullDataLoaded(true);
      fullLoadTimerRef.current = setTimeout(() => {
        console.log('⏰ Full-load period expired, reverting to last 5 days');
        localStorage.removeItem(FULL_LOAD_EXPIRY_KEY);
        setIsFullDataLoaded(false);
        localStorage.removeItem(SALES_CACHE_KEY);
        localStorage.removeItem(SALES_TIMESTAMP_KEY);
        if (user) fetchSalesData(true, false);
      }, remaining);
    }

    fetchProductsData();
    if (user) fetchSalesData();

    return () => {
      if (fullLoadTimerRef.current) clearTimeout(fullLoadTimerRef.current);
    };
  }, [user, authLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  const value = {
    salesData,
    productsData,
    salesLoading,
    productsLoading,
    loading: salesLoading || productsLoading || authLoading,
    salesError,
    productsError,
    error: salesError || productsError,
    isFullDataLoaded,
    user,
    authLoading,
    refreshData,
    fetchSalesData,
    fetchProductsData,
    invalidateCache,
    loadAllData,
    unloadAllData,
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
};

// Custom hook to use the DataContext
export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

// Export the context itself for advanced usage
export default DataContext;
